import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bug, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../styles/ThemeContext';
interface QAReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenshotUrl: string;
  onSubmit: (data: {
    category: string;
    priority: string;
    notes: string;
  }) => Promise<void>;
}
const CATEGORIES = [{
  id: 'bug',
  label: 'Bug'
}, {
  id: 'feature',
  label: 'Feature'
}, {
  id: 'visual',
  label: 'Visual'
}, {
  id: 'other',
  label: 'Other'
}];
const PRIORITIES = [{
  id: 'critical',
  label: 'Critical',
  color: 'text-red-500',
  bg: 'bg-red-500/10',
  border: 'border-red-500/20'
}, {
  id: 'high',
  label: 'High',
  color: 'text-orange-500',
  bg: 'bg-orange-500/10',
  border: 'border-orange-500/20'
}, {
  id: 'medium',
  label: 'Medium',
  color: 'text-yellow-500',
  bg: 'bg-yellow-500/10',
  border: 'border-yellow-500/20'
}, {
  id: 'low',
  label: 'Low',
  color: 'text-emerald-500',
  bg: 'bg-emerald-500/10',
  border: 'border-emerald-500/20'
}];
export function QAReportModal({
  isOpen,
  onClose,
  screenshotUrl,
  onSubmit
}: QAReportModalProps) {
  const {
    isDark
  } = useTheme();
  const [category, setCategory] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        category,
        priority,
        notes
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
        // Reset form
        setCategory('bug');
        setPriority('medium');
        setNotes('');
      }, 2000);
    } catch (error) {
      console.error('Failed to submit report', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-1569">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[100] flex flex-col" data-id="element-1570">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-20 p-6 bg-gradient-to-b from-black/80 to-transparent" data-id="element-1571">
            <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-1572">
              <div className="flex items-center gap-3" data-id="element-1573">
                <div className="p-2 rounded-full bg-red-500/20 text-red-400" data-id="element-1574">
                  <Bug className="w-5 h-5" data-id="element-1575" />
                </div>
                <div data-id="element-1576">
                  <h2 className="text-xl font-light text-white" data-id="element-1577">
                    QA <span className="font-bold text-red-400" data-id="element-1578">Report</span>
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-1579">
                <X className="w-6 h-6" data-id="element-1580" />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center p-6 pt-24 pb-12 overflow-y-auto" data-id="element-1581">
            <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-1582">
              {/* Left Column: Screenshot Preview */}
              <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="relative w-full aspect-[16/10] rounded-xl overflow-hidden shadow-2xl bg-slate-900 border border-white/10" data-id="element-1583">
                <img src={screenshotUrl} alt="Current View Screenshot" className="absolute inset-0 w-full h-full object-cover" data-id="element-1584" />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider" data-id="element-1585">
                  Screenshot Attached
                </div>
              </motion.div>

              {/* Right Column: Form */}
              <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.4,
            delay: 0.2
          }} className={`p-8 rounded-2xl backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/10 border-white/20'}`} data-id="element-1586">
                <form onSubmit={handleSubmit} className="space-y-8" data-id="element-1587">
                  {/* Category Selection */}
                  <div className="space-y-3" data-id="element-1588">
                    <label className="text-sm font-bold uppercase tracking-wider text-white/70" data-id="element-1589">
                      Category
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-1590">
                      {CATEGORIES.map(cat => <button key={cat.id} type="button" onClick={() => setCategory(cat.id)} className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${category === cat.id ? 'bg-white text-black border-white shadow-lg scale-105' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white'}`} data-id="element-1591">
                          {cat.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Priority Selection */}
                  <div className="space-y-3" data-id="element-1592">
                    <label className="text-sm font-bold uppercase tracking-wider text-white/70" data-id="element-1593">
                      Priority
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-1594">
                      {PRIORITIES.map(pri => <button key={pri.id} type="button" onClick={() => setPriority(pri.id)} className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border flex items-center justify-center gap-2 ${priority === pri.id ? `${pri.bg} ${pri.color} ${pri.border} shadow-lg scale-105 ring-1 ring-current` : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white/80'}`} data-id="element-1595">
                          <div className={`w-2 h-2 rounded-full ${priority === pri.id ? 'bg-current' : pri.color.replace('text-', 'bg-')}`} data-id="element-1596" />
                          {pri.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-3" data-id="element-1597">
                    <label className="text-sm font-bold uppercase tracking-wider text-white/70" data-id="element-1598">
                      Notes
                    </label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe the issue, steps to reproduce, or feature request details..." rows={5} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-red-400 focus:bg-white/10 transition-all resize-none custom-scrollbar" required data-id="element-1599" />
                  </div>

                  {/* Submit Button */}
                  <button type="submit" disabled={isSubmitting || submitSuccess || !notes.trim()} className={`w-full py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${submitSuccess ? 'bg-emerald-500 text-white' : 'bg-red-500 hover:bg-red-400 text-white disabled:opacity-50 disabled:hover:bg-red-500'}`} data-id="element-1600">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1601" /> : submitSuccess ? <>
                        <CheckCircle2 className="w-5 h-5" data-id="element-1602" />
                        Report Sent
                      </> : <>
                        <Send className="w-5 h-5" data-id="element-1603" />
                        Send Report to ResVR
                      </>}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}